import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Brain, Menu, X, Crown } from "lucide-react";
import { useStoredAuth } from "@/hooks/useStoredAuth";
import { clearAuth } from '@/lib/authStore';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from 'wouter'
import { useToast } from "@/hooks/use-toast";
import AuthModal from "./AuthModal";

const envBaseUrl = import.meta.env.VITE_API_BASE_URL || "";
const API_BASE_URL =
  envBaseUrl && !envBaseUrl.startsWith("http") && !envBaseUrl.startsWith("/")
    ? `http://${envBaseUrl}`
    : envBaseUrl;

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const { user, isAuthenticated, isLoading } = useStoredAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMobileMenuOpen(false);
  };

  const logoutMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch( `${API_BASE_URL}${"/api/auth/logout"}`, {
        method: "POST",
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Error al cerrar sesión");
      }
      
      return await response.json();
    },
    onSuccess: () => {
      try { clearAuth(); } catch (e) {}
      toast({
        title: "Sesión cerrada",
        description: "Has cerrado sesión correctamente.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      try { setLocation('/'); } catch (e) {}
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleLogin = () => {
    setAuthModalOpen(true);
  };

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <Brain className="text-white text-lg" />
            </div>
            <h1> 
            <button
              onClick={() => setLocation('/')}
              className="text-xl font-bold text-gray-900">La IA Fácil</button></h1>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('tools')}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium flex items-center"
            >
              Herramientas IA
              {!user?.isPremium && (
                <span className="ml-1 text-xs bg-amber-400 text-white px-1.5 py-0.5 rounded-full">Premium</span>
              )}
            </button>
            <button
              onClick={() => scrollToSection('blog')}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
            >
              Blog
            </button>
            <button
              onClick={() => scrollToSection('notebooks')}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium flex items-center"
            >
              Notebooks
              {!user?.isPremium && (
                <span className="ml-1 text-xs bg-amber-400 text-white px-1.5 py-0.5 rounded-full">Premium</span>
              )}
            </button>
            <button
              onClick={() => scrollToSection('papers')}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
            >
              Papers
            </button>
          </nav>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {isLoading ? (
              <div className="w-20 h-8 bg-gray-200 animate-pulse rounded"></div>
            ) : isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  {user?.profileImageUrl && (
                    <img
                      src={user.profileImageUrl}
                      alt="Profile"
                      className="w-8 h-8 rounded-full"
                    />
                  )}
                  <span className="text-gray-700 font-medium">
                    {user?.firstName || user?.email}
                  </span>
                  {user?.isPremium && (
                    <Crown className="w-4 h-4 text-amber-500" />
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  className="text-gray-700 hover:text-blue-600"
                >
                  Cerrar Sesión
                </Button>
              </div>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={handleLogin}
                  className="text-gray-700 hover:text-blue-600 font-medium"
                >
                  Iniciar Sesión
                </Button>
                <Button
                  onClick={handleLogin}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:opacity-90 font-medium"
                >
                  Registrarse
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="text-lg" /> : <Menu className="text-lg" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-3 space-y-3">
            <button
              onClick={() => scrollToSection('tools')}
              className="block text-gray-700 font-medium w-full text-left"
            >
              Herramientas IA{" "}
              {!user?.isPremium && (
                <span className="text-xs bg-amber-400 text-white px-1.5 py-0.5 rounded-full ml-1">Premium</span>
              )}
            </button>
            <button
              onClick={() => scrollToSection('blog')}
              className="block text-gray-700 font-medium w-full text-left"
            >
              Blog
            </button>
            <button
              onClick={() => scrollToSection('notebooks')}
              className="block text-gray-700 font-medium w-full text-left"
            >
              Notebooks{" "}
              {!user?.isPremium && (
                <span className="text-xs bg-amber-400 text-white px-1.5 py-0.5 rounded-full ml-1">Premium</span>
              )}
            </button>
            <button
              onClick={() => scrollToSection('papers')}
              className="block text-gray-700 font-medium w-full text-left"
            >
              Papers
            </button>
            <div className="pt-3 border-t border-gray-200 space-y-2">
              {isAuthenticated ? (
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  className="block w-full"
                >
                  Cerrar Sesión
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={handleLogin}
                    className="block w-full text-left text-gray-700 font-medium"
                  >
                    Iniciar Sesión
                  </Button>
                  <Button
                    onClick={handleLogin}
                    className="block w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium"
                  >
                    Registrarse
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      <AuthModal 
        isOpen={authModalOpen} 
        onClose={() => setAuthModalOpen(false)} 
      />
    </header>
  );
}
